import React from "react";
import { cn } from "../../../common/u1/cn";
import { formatAed } from "./billingFormat";
import {
  Dirham,
  StarIcon,
  ClockIcon,
  CreditCardIcon,
  ArrowUpRight,
} from "../../../../assets/ui/icons";

/* The active plan, on the design's dark card. */
export const PlanSummaryCard = ({
  planName,
  statusLabel,
  cadence,
  cadenceTone = "normal",
  card,
  /* "AED" draws the Dirham glyph; anything else is printed as written. The
     legacy plans are quoted in dollars on the live screen, so the card cannot
     assume one currency. */
  currency = "AED",
  amount,
  period,
  meta,
  children,
  action,
  className = "",
}) => (
  <section
    aria-labelledby="u1-plan-heading"
    className={cn(
      "overflow-hidden rounded-[14px] p-6 text-white bg-[image:var(--plan-grad)] sm:p-7",
      className
    )}
  >
    <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2.5">
          <h3
            id="u1-plan-heading"
            className="m-0 text-[22px] font-bold tracking-[-.01em] font-sans sm:text-[25px]"
          >
            {planName}
          </h3>
          {statusLabel && (
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-2.5 py-1",
                "text-[10.5px] font-bold uppercase tracking-[.08em] font-sans",
                "border-[var(--plan-pill-line)] bg-[var(--plan-pill-bg)] text-[var(--plan-pill-ink)]"
              )}
            >
              <StarIcon />
              {statusLabel}
            </span>
          )}
        </div>

        {cadence && (
          <p
            className={cn(
              "mt-3 mb-0 flex items-center gap-2 text-[13.5px] font-sans",
              cadenceTone === "warning" ? "text-[var(--plan-warn)]" : "text-[var(--plan-body)]"
            )}
          >
            <span className="shrink-0 opacity-80">
              <ClockIcon size={15} />
            </span>
            {cadence}
          </p>
        )}

        {/* Only shown when the row carries a card — transcribed guard. */}
        {card?.last4 && (
          <p className="mt-1.5 mb-0 flex items-center gap-2 text-[13px] text-[var(--plan-micro)] font-sans">
            <span className="shrink-0 opacity-80">
              <CreditCardIcon size={15} />
            </span>
            Card · {card.brand} •••• {card.last4}
          </p>
        )}
      </div>

      {typeof amount === "number" && (
        <div className="flex shrink-0 items-baseline gap-2 sm:flex-col sm:items-end sm:gap-0">
          <span className="flex items-baseline text-[34px] font-bold leading-none font-sans sm:text-[40px]">
            {currency === "AED" ? <Dirham /> : <span className="mr-1.5">{currency}</span>}
            {formatAed(amount)}
          </span>
          {period && (
            <span className="text-[13px] text-[var(--plan-body)] font-sans sm:mt-2">
              {period}
            </span>
          )}
        </div>
      )}
    </div>

    {(meta || children || action) && (
      <>
        <hr className="my-5 h-px border-0 bg-[var(--plan-rule)]" />
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {(meta || children) && (
            <div className="flex min-w-0 flex-col gap-1.5 text-[13.5px] text-[var(--plan-body)] font-sans">
              {children}
              {meta && <p className="m-0">{meta}</p>}
            </div>
          )}
          {action && (
            <button
              type="button"
              onClick={action.onClick}
              disabled={action.disabled}
              className={cn(
                "inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-[10px] px-5",
                "border border-white/45 bg-transparent text-[14px] font-semibold text-white font-sans",
                "transition-colors hover:bg-white/10",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70",
                "disabled:cursor-not-allowed disabled:opacity-60"
              )}
            >
              {action.label}
              <ArrowUpRight width={13} height={12} />
            </button>
          )}
        </div>
      </>
    )}
  </section>
);

export default PlanSummaryCard;
